import React, {
  createContext,
  useContext,
  useEffect,
  type ReactNode,
} from "react";
import { useLocation } from "react-router-dom";

import appConfig from "@/config/app-config";
import { useGdprTracking, useTvSource } from "@/hooks";
import {
  bootstrapSession,
  setAdjustAccountId,
  setAdjustAccountScope,
  syncAdjust,
  trackEngagement,
  trackOnboardingComplete,
  trackSignup,
} from "@/utils/adjust";
import { isEmbedPath } from "@/utils/embed-path";

import { useAuth } from "./auth/useAuth";

/**
 * Adjust attribution wiring. Mirrors AnalyticsProvider's GDPR gating.
 *
 * const { trackEngagement } = useAdjust();
 * trackEngagement("like");
 */
interface AdjustProps {
  trackSignup: typeof trackSignup;
  trackOnboardingComplete: typeof trackOnboardingComplete;
  trackEngagement: typeof trackEngagement;
}

// Default to the real helpers (safe no-ops until the SDK is ready), so calling
// useAdjust() outside an AdjustProvider — e.g. in tests — never throws.
const AdjustContext = createContext<AdjustProps>({
  trackSignup,
  trackOnboardingComplete,
  trackEngagement,
});
const useAdjust = () => useContext(AdjustContext);

const AdjustProvider = ({ children }: { children: ReactNode }) => {
  const location = useLocation();
  const { performance } = useGdprTracking();
  const isTvSource = useTvSource();
  const { currentAccount } = useAuth();

  const isEmbedRoute = isEmbedPath(location.pathname);
  const token = appConfig.ADJUST_APP_TOKEN;
  const canTrack = !isTvSource && !isEmbedRoute && !!token;

  // Scope to the account first so flags resolve correctly, reconcile consent,
  // then bootstrap the session. Re-runs on account id so d7 sees the signup flag.
  useEffect(() => {
    if (!canTrack) return;

    setAdjustAccountScope(currentAccount?.id ?? null);
    syncAdjust({ consent: !!performance });

    if (performance) {
      // Attach account_id before bootstrapping so the d7 session event carries it.
      if (currentAccount?.id) {
        setAdjustAccountId(currentAccount.id);
      }
      bootstrapSession();
    }
  }, [canTrack, performance, currentAccount?.id]);

  return (
    <AdjustContext.Provider
      value={{ trackSignup, trackOnboardingComplete, trackEngagement }}
    >
      {children}
    </AdjustContext.Provider>
  );
};

export { useAdjust, AdjustContext };
export default AdjustProvider;
